import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button, Card, Cluster, FormField, Grid, ProgressBar, Select, Stack } from "@salesapp/ui";
import { TipHeading, TipKicker } from "./AdminTip";
import { api, ApiError } from "./api";
import { compactUsd, countryLabel, formatBytes, formatIp, formatUtc, formatUptime, money } from "./format";
import { Flash } from "./Flash";

interface Kpis {
  customers: number;
  active_companies: number;
  active_users: number;
  never_logged_in: number;
  live_sessions: number;
  licensed_seats: number;
  assigned_seats: number;
  ai_budget_cents: number;
  ai_used_cents: number;
}

interface ChartPoint {
  start: string | null;
  label: string;
  amount_cents: number;
}

interface AnnuityChart {
  title: string;
  total_cents: number;
  currency: string;
  points: ChartPoint[];
}

interface ChurnMonth {
  month: string;
  label: string;
  signups: number;
  cancellations: number;
  suspensions: number;
}

interface HealthService {
  name: string;
  status: string;
  latency_ms: number | null;
  detail: string | null;
  meta: Record<string, unknown>;
}

interface Dashboard {
  as_of: string | null;
  kpis: Kpis;
  annuity: {
    currency: string;
    mrr_cents: number;
    arr_cents: number;
    contracted_licenses: number;
    trial_licenses: number;
    plan_mix: Array<{ tier: string; count: number }>;
    charts: {
      next_month: AnnuityChart;
      next_quarter: AnnuityChart;
      next_year: AnnuityChart;
    };
  };
  churn: {
    months: ChurnMonth[];
    recent: Array<{ kind: string; at: string | null; org_id: string; display_name: string }>;
  };
  watch: {
    ending_soon: Array<{
      org_id: string;
      display_name: string;
      tier: string;
      grant_source: string;
      access_ends_at: string | null;
    }>;
  };
  health: {
    status: string;
    platform: string;
    environment: string;
    region: string;
    services: HealthService[];
  };
  threats?: {
    open: number;
    critical_open: number;
    last_24h: number;
    lockouts_24h: number;
    failed_logins_24h: number;
    csrf_24h: number;
  };
  retrieval?: Retrieval;
  presence?: Presence;
  storage?: StorageSummary;
  acquisition?: {
    window_days: number;
    signups: number;
    sources: Array<{ source: string; medium: string | null; landing: string | null; signups: number }>;
  };
}

interface RetrievalObject {
  kind: string;
  count: number;
  error_count: number;
  p50_ms: number | null;
  p95_ms: number | null;
  slow_count: number;
  status: string;
}

interface RetrievalProbe {
  status: string;
  latency_ms: number | null;
  detail: string | null;
}

interface Retrieval {
  window_seconds: number;
  slow_ms: number;
  critical_ms: number;
  count: number;
  error_count: number;
  slow_count: number;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  max_ms: number | null;
  status: string;
  objects: RetrievalObject[];
  probe: RetrievalProbe;
  basis: string;
}

interface PresenceSession {
  session_id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  org_id: string | null;
  org_name: string | null;
  client: string;
  ip: string | null;
  country: string | null;
  created_at: string | null;
  last_used_at: string | null;
  status: "active" | "idle";
  is_platform_staff: boolean;
}

interface Presence {
  window_seconds: number;
  active_now: number;
  signed_in: number;
  sessions: PresenceSession[];
}

interface StorageSummary {
  bytes: number;
  mb: number;
  basis: string;
}

export function DashboardPage() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<Dashboard>("/operator/dashboard")
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Dashboard could not be loaded."));
  }, []);

  if (!data) {
    return error ? <p className="admin-error">{error}</p> : <p>Loading…</p>;
  }

  const { kpis, annuity, churn, watch, health } = data;
  const acquisition = data.acquisition ?? { window_days: 90, signups: 0, sources: [] };
  const presence = data.presence ?? { window_seconds: 900, active_now: 0, signed_in: 0, sessions: [] };
  const seatPct = kpis.licensed_seats ? Math.round((kpis.assigned_seats / kpis.licensed_seats) * 100) : 0;
  const aiPct = kpis.ai_budget_cents ? Math.round((kpis.ai_used_cents / kpis.ai_budget_cents) * 100) : 0;
  const churnCancels = churn.months.reduce((sum, row) => sum + row.cancellations, 0);
  const churnSignups = churn.months.reduce((sum, row) => sum + row.signups, 0);

  return (
    <Stack gap={5}>
      <Cluster gap={3} align="center">
        <h2>Dashboard</h2>
        <p className="admin-muted">As of {formatUtc(data.as_of)} · {health.environment} / {health.region}</p>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}

      <PlatformHealthSection health={health} />

      <Grid columns={3} gap={4}>
        <KpiTile
          label="Total customers"
          tip="All tenant organizations on the platform, active or not."
          value={kpis.customers}
          hint={`${kpis.active_companies} active now`}
        />
        <KpiTile
          label="Active customers"
          tip="Paying and partner organizations that can still sign in. Trials, cancelled organizations, and expired licences are not included."
          value={kpis.active_companies}
          hint={`${annuity.trial_licenses} on trial`}
        />
        <KpiTile
          label="Active users"
          tip="People with an active membership who can log into an org console."
          value={kpis.active_users}
          hint={`${kpis.never_logged_in} never logged on`}
        />
      </Grid>

      <Grid columns={3} gap={4}>
        <KpiTile
          label="Annuity base (MRR)"
          tip="Monthly recurring revenue from contracted CRM licences. Trials are excluded until they convert."
          value={money(annuity.mrr_cents)}
          hint={`${annuity.contracted_licenses} contracted licences`}
        />
        <KpiTile
          label="Forward ARR"
          tip="Annualized run rate from the same contracted base: MRR × 12."
          value={money(annuity.arr_cents)}
          hint="Monthly equivalent × 12"
        />
        <KpiTile
          label="Live sessions"
          tip="Sessions with a recent heartbeat. Active means used in the last 15 minutes."
          value={kpis.live_sessions}
          hint={`${presence.active_now} active in the last 15 minutes`}
        />
        <KpiTile
          label="People online"
          tip="People currently signed in. Open Current online users for the searchable table."
          value={presence.signed_in}
          hint={`${presence.active_now} active now`}
        />
        <KpiTile
          label="Storage"
          tip="Total measured production storage footprint for tenant data."
          value={`${data.storage?.mb ?? 0} MB`}
          hint={data.storage?.basis ?? "Storage usage unavailable"}
        />
      </Grid>

      <section>
        <TipHeading
          label="Signups by source"
          tip="Public signups attributed to UTM source, medium, and landing page from saleslox.com."
        >
          <p className="admin-muted">Last {acquisition.window_days} days · {acquisition.signups} public signups</p>
        </TipHeading>
        <Card>
          <table>
            <thead>
              <tr><th>Source</th><th>Medium</th><th>Landing page</th><th>Signups</th></tr>
            </thead>
            <tbody>
              {acquisition.sources.length === 0 && (
                <tr><td colSpan={4} className="admin-muted">No public signups in this window yet. Source is captured from UTM links on saleslox.com.</td></tr>
              )}
              {acquisition.sources.map((row) => (
                <tr key={`${row.source}-${row.medium ?? ""}-${row.landing ?? ""}`}>
                  <td>{row.source}</td>
                  <td>{row.medium || "—"}</td>
                  <td>{row.landing || "—"}</td>
                  <td>{row.signups}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>

      <section>
        <TipHeading
          label="Annuity base — expected collections"
          tip="Forecast of collections from contracted CRM licences over the next month, quarter, and year."
        />
        <p className="admin-muted">Contracted CRM licences only. Trials are excluded until they convert.</p>
        <div className="admin-chart-row">
          <AnnuityChartCard chart={annuity.charts.next_month} />
          <AnnuityChartCard chart={annuity.charts.next_quarter} />
          <AnnuityChartCard chart={annuity.charts.next_year} />
        </div>
      </section>

      <Grid columns={2} gap={4}>
        <Card>
          <Stack gap={3}>
            <TipHeading
              label="Churn timeline"
              tip="Signups, cancels, and suspensions over the last 12 months, plus the recent event list."
            >
              <p className="admin-muted">{churnSignups} signups · {churnCancels} cancels over 12 months</p>
            </TipHeading>
            <ChurnChart months={churn.months} />
            <Cluster gap={3} align="center">
              <span className="admin-legend"><span className="admin-swatch admin-swatch--up" /> Signups</span>
              <span className="admin-legend"><span className="admin-swatch admin-swatch--down" /> Cancels</span>
              <span className="admin-legend"><span className="admin-swatch admin-swatch--warn" /> Suspensions</span>
            </Cluster>
            <table>
              <thead>
                <tr><th>When (UTC)</th><th>Event</th><th>Customer</th></tr>
              </thead>
              <tbody>
                {churn.recent.length === 0 && (
                  <tr><td colSpan={3} className="admin-muted">No signup or cancel events yet.</td></tr>
                )}
                {churn.recent.map((row) => (
                  <tr key={`${row.org_id}-${row.at}-${row.kind}`}>
                    <td>{formatUtc(row.at)}</td>
                    <td><StatusBadge status={row.kind === "cancel" ? "deactivated" : "active"}>{row.kind}</StatusBadge></td>
                    <td><Link to={`/customers/${row.org_id}`}>{row.display_name}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Card>
        <Card>
          <Stack gap={3}>
            <TipHeading
              label="What needs attention"
              tip="Seat fill, AI spend pressure, plan mix, and licences ending in the next 30 days."
            />
            <ProgressBar
              value={seatPct}
              tone={seatPct > 90 ? "warning" : "primary"}
              label={`Seat fill ${kpis.assigned_seats}/${kpis.licensed_seats}`}
            />
            <ProgressBar
              value={aiPct}
              tone={aiPct > 80 ? "warning" : "success"}
              label={`AI spend ${money(kpis.ai_used_cents)} of ${money(kpis.ai_budget_cents)}`}
            />
            <h4 className="admin-history-title">Plan mix</h4>
            <Cluster gap={2}>
              {annuity.plan_mix.length === 0 && <p className="admin-muted">No active CRM plans.</p>}
              {annuity.plan_mix.map((row) => (
                <span key={row.tier} className="admin-badge">{row.tier} · {row.count}</span>
              ))}
            </Cluster>
            <h4 className="admin-history-title">Access ending in 30 days</h4>
            <table>
              <thead>
                <tr><th>Customer</th><th>Plan</th><th>Ends (UTC)</th></tr>
              </thead>
              <tbody>
                {watch.ending_soon.length === 0 && (
                  <tr><td colSpan={3} className="admin-muted">Nothing expiring in the next 30 days.</td></tr>
                )}
                {watch.ending_soon.map((row) => (
                  <tr key={row.org_id}>
                    <td><Link to={`/customers/${row.org_id}`}>{row.display_name}</Link></td>
                    <td>{row.tier} · {row.grant_source}</td>
                    <td>{formatUtc(row.access_ends_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Card>
      </Grid>

      <section>
        <TipHeading
          label="Threat detection"
          tip="Security events that need operator review or show recent attack pressure."
        >
          <Link to="/threats">Open threats</Link>
        </TipHeading>
        <Grid columns={3} gap={4}>
          <KpiTile
            label="Open threats"
            tip="Unresolved threat events still waiting for an operator decision."
            value={data.threats?.open ?? 0}
            hint={`${data.threats?.critical_open ?? 0} critical`}
          />
          <KpiTile
            label="Lockouts (24h)"
            tip="Accounts locked after failed sign-ins in the last day."
            value={data.threats?.lockouts_24h ?? 0}
            hint={`${data.threats?.failed_logins_24h ?? 0} failed sign-ins`}
          />
          <KpiTile
            label="CSRF blocks (24h)"
            tip="Requests rejected for a missing or invalid CSRF token in the last day."
            value={data.threats?.csrf_24h ?? 0}
            hint={`${data.threats?.last_24h ?? 0} events in 24h`}
          />
        </Grid>
      </section>

      <RetrievalPanel retrieval={data.retrieval} />


    </Stack>
  );
}

export function CurrentOnlineUsersPage() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<Dashboard>("/operator/dashboard")
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Online users could not be loaded."));
  }, []);

  if (!data) {
    return error ? <p className="admin-error">{error}</p> : <p>Loadingâ€¦</p>;
  }

  const presence = data.presence ?? { window_seconds: 900, active_now: 0, signed_in: 0, sessions: [] };
  return <PresencePanel presence={presence} />;
}

function PlatformHealthSection({ health }: { health: Dashboard["health"] }) {
  return (
    <section>
      <TipHeading
        label="Platform health"
        tip="Live checks for API, database, Redis, and related platform services."
      >
        <StatusBadge status={health.status}>{health.status}</StatusBadge>
        <p className="admin-muted">{health.platform} platform</p>
      </TipHeading>
      <div className="admin-health-grid">
        {health.services.map((service) => (
          <Card key={service.name} padding="sm">
            <Stack gap={2}>
              <Cluster gap={2} align="center">
                <strong>{service.name}</strong>
                <StatusBadge status={service.status}>{service.status}</StatusBadge>
              </Cluster>
              <p className="admin-muted">
                {service.detail || "â€”"}
                {service.latency_ms != null ? ` Â· ${service.latency_ms} ms` : ""}
              </p>
              <ServiceMeta service={service} />
            </Stack>
          </Card>
        ))}
      </div>
    </section>
  );
}

function presenceUserLabel(row: PresenceSession): string {
  return row.display_name || row.email || row.user_id;
}

const NO_COMPANY = "__none__";
const UNKNOWN_COUNTRY = "__unknown__";

function presenceCompanyKey(row: PresenceSession): string {
  return row.org_id || NO_COMPANY;
}

function presenceCompanyLabel(row: PresenceSession): string {
  if (!row.org_id) return "No company";
  return row.org_name || row.org_id;
}

function presenceCountryKey(row: PresenceSession): string {
  return row.country || UNKNOWN_COUNTRY;
}

function PresencePanel({ presence }: { presence: Presence }) {
  const [userFilter, setUserFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  const userOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of presence.sessions) {
      if (!seen.has(row.user_id)) seen.set(row.user_id, presenceUserLabel(row));
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [presence.sessions]);

  const companyOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of presence.sessions) {
      const key = presenceCompanyKey(row);
      if (!seen.has(key)) seen.set(key, presenceCompanyLabel(row));
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [presence.sessions]);

  const countryOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of presence.sessions) {
      const key = presenceCountryKey(row);
      if (!seen.has(key)) seen.set(key, countryLabel(row.country));
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [presence.sessions]);

  const filteredSessions = useMemo(
    () =>
      presence.sessions.filter((row) => {
        if (userFilter && row.user_id !== userFilter) return false;
        if (companyFilter && presenceCompanyKey(row) !== companyFilter) return false;
        if (countryFilter && presenceCountryKey(row) !== countryFilter) return false;
        return true;
      }),
    [presence.sessions, userFilter, companyFilter, countryFilter],
  );

  const filtersDirty = Boolean(userFilter || companyFilter || countryFilter);
  const filteredActive = filteredSessions.filter((row) => row.status === "active").length;

  return (
    <section>
      <TipHeading
        label="People on the system now"
        tip="Who is signed in right now, and whether they were active in the last 15 minutes. Filter by user, company, or country to focus the list."
      >
        <p className="admin-muted">
          {filtersDirty
            ? `${filteredActive} active · ${filteredSessions.length} matching · ${presence.signed_in} signed in`
            : `${presence.active_now} active in the last 15 minutes · ${presence.signed_in} signed in`}
        </p>
      </TipHeading>
      <Stack gap={3}>
        <Card>
          <Cluster gap={3} align="end">
            <FormField
              label="User"
              tooltip="Show sessions for one person. Leave All to include everyone signed in."
            >
              <Select
                aria-label="Filter by user"
                value={userFilter}
                onChange={(event) => setUserFilter(event.target.value)}
              >
                <option value="">All</option>
                {userOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="Company"
              tooltip="Show sessions for one tenant organization. No company covers platform-staff sessions."
            >
              <Select
                aria-label="Filter by company"
                value={companyFilter}
                onChange={(event) => setCompanyFilter(event.target.value)}
              >
                <option value="">All</option>
                {companyOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="Country"
              tooltip="Show sessions from one country code. Unknown covers missing geo."
            >
              <Select
                aria-label="Filter by country"
                value={countryFilter}
                onChange={(event) => setCountryFilter(event.target.value)}
              >
                <option value="">All</option>
                {countryOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="Filters"
              tooltip="Clear user, company, and country filters."
            >
              <Button
                type="button"
                variant="secondary"
                disabled={!filtersDirty}
                onClick={() => {
                  setUserFilter("");
                  setCompanyFilter("");
                  setCountryFilter("");
                }}
              >
                Clear
              </Button>
            </FormField>
          </Cluster>
        </Card>
        <Card>
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>User</th>
                <th>Company</th>
                <th>Where</th>
                <th>IP</th>
                <th>Country</th>
                <th>Last used (UTC)</th>
              </tr>
            </thead>
            <tbody>
              {presence.sessions.length === 0 && (
                <tr>
                  <td colSpan={7} className="admin-muted">Nobody is signed in right now.</td>
                </tr>
              )}
              {presence.sessions.length > 0 && filteredSessions.length === 0 && (
                <tr>
                  <td colSpan={7} className="admin-muted">No sessions match these filters.</td>
                </tr>
              )}
              {filteredSessions.map((row) => (
                <tr key={row.session_id}>
                  <td>
                    <span className={`admin-badge ${row.status === "active" ? "is-ok" : "is-idle"}`}>
                      {row.status === "active" ? "Active" : "Idle"}
                    </span>
                  </td>
                  <td>
                    {presenceUserLabel(row)}
                    {row.email && row.display_name ? (
                      <p className="admin-muted">{row.email}</p>
                    ) : null}
                    {row.is_platform_staff ? (
                      <p className="admin-muted">Platform staff</p>
                    ) : null}
                  </td>
                  <td>
                    {row.org_id ? (
                      <Link to={`/customers/${row.org_id}`}>{row.org_name || row.org_id}</Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{row.client}</td>
                  <td><code>{formatIp(row.ip)}</code></td>
                  <td>{countryLabel(row.country)}</td>
                  <td className="admin-readonly">{formatUtc(row.last_used_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </Stack>
    </section>
  );
}

function RetrievalPanel({ retrieval }: { retrieval: Retrieval | undefined }) {
  if (!retrieval) return null;
  const target = retrieval.slow_ms || 200;
  const p95 = retrieval.p95_ms;
  const budgetPct = p95 == null ? 0 : Math.min(100, Math.round((p95 / target) * 100));
  const tone = retrieval.status === "critical" ? "danger" : retrieval.status === "slow" ? "warning" : "success";
  const idle = retrieval.status === "idle" || retrieval.count === 0;
  return (
    <section>
      <Stack gap={4}>
      <TipHeading
        label="Transaction retrieval"
        tip="How fast authenticated CRM list and detail reads are completing for customers."
      >
        <StatusBadge status={retrieval.status}>{retrieval.status}</StatusBadge>
      </TipHeading>
      <p className="admin-muted">{retrieval.basis}</p>
      <Grid columns={3} gap={4}>
        <KpiTile
          label="Observed p95"
          tip="95th percentile latency for authenticated CRM list and detail GETs."
          value={ms(p95)}
          hint={`Target under ${target} ms · p50 ${ms(retrieval.p50_ms)}`}
        />
        <KpiTile
          label="CRM GETs (15 min)"
          tip="Authenticated CRM read traffic in the last 15 minutes, including slow and error counts."
          value={retrieval.count}
          hint={`${retrieval.slow_count} slow · ${retrieval.error_count} server errors`}
        />
        <KpiTile
          label="List-shape probe"
          tip="Synthetic opportunity-list query timing as sales_rw with no tenant context."
          value={ms(retrieval.probe?.latency_ms)}
          hint={retrieval.probe?.detail || "Opportunity list SQL as sales_rw, no tenant context"}
        />
      </Grid>
      {!idle && (
        <Card>
          <Stack gap={3}>
            <ProgressBar
              value={budgetPct}
              tone={tone}
              label={`p95 used ${budgetPct}% of the ${target} ms budget`}
            />
            <table>
              <thead>
                <tr>
                  <th>Object</th>
                  <th>Calls</th>
                  <th>p50</th>
                  <th>p95</th>
                  <th>Slow</th>
                  <th>Errors</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {retrieval.objects.map((row) => (
                  <tr key={row.kind}>
                    <td>{row.kind}</td>
                    <td>{row.count}</td>
                    <td>{ms(row.p50_ms)}</td>
                    <td>{ms(row.p95_ms)}</td>
                    <td>{row.slow_count}</td>
                    <td>{row.error_count}</td>
                    <td><StatusBadge status={row.status}>{row.status}</StatusBadge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Card>
      )}
      {idle && (
        <p className="admin-muted">No authenticated CRM list or detail GETs in the last 15 minutes.</p>
      )}
      </Stack>
    </section>
  );
}

function ms(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value} ms`;
}

function KpiTile({
  label,
  tip,
  value,
  hint,
}: {
  label: string;
  tip: string;
  value: number | string;
  hint: string;
}) {
  return (
    <Card>
      <Stack gap={2}>
        <TipKicker label={label} tip={tip} />
        <p className="admin-kpi">{value}</p>
        <p className="admin-muted">{hint}</p>
      </Stack>
    </Card>
  );
}

function AnnuityChartCard({ chart }: { chart: AnnuityChart }) {
  return (
    <Card>
      <Stack gap={3}>
        <Cluster gap={3} align="center">
          <TipHeading
            label={chart.title}
            tip="Expected collections from contracted CRM licences in this window. Trials are excluded."
          />
          <p className="admin-kpi admin-kpi--chart">{compactUsd(chart.total_cents)}</p>
        </Cluster>
        <p className="admin-muted">Expected collections in this window</p>
        <BarChart points={chart.points} />
      </Stack>
    </Card>
  );
}

function BarChart({ points }: { points: ChartPoint[] }) {
  const max = Math.max(...points.map((point) => point.amount_cents), 1);
  const barWidth = 100 / Math.max(points.length, 1);
  const labelEvery = points.length > 8 ? 2 : 1;
  return (
    <svg viewBox="0 0 100 56" className="admin-chart-svg" role="img" aria-label="Annuity collections">
      {points.map((point, index) => {
        const height = (point.amount_cents / max) * 42;
        const x = index * barWidth + barWidth * 0.18;
        return (
          <g key={`${point.start}-${index}`}>
            <rect
              x={x}
              y={46 - height}
              width={barWidth * 0.64}
              height={height || 0.4}
              rx="0.8"
              className="admin-chart-bar"
            />
            {index % labelEvery === 0 && (
              <text x={index * barWidth + barWidth / 2} y="54" textAnchor="middle" className="admin-chart-label">
                {point.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function ChurnChart({ months }: { months: ChurnMonth[] }) {
  const max = Math.max(...months.flatMap((row) => [row.signups, row.cancellations, row.suspensions]), 1);
  const toY = (value: number) => 42 - (value / max) * 34;
  const step = months.length > 1 ? 100 / (months.length - 1) : 100;
  const path = (key: keyof Pick<ChurnMonth, "signups" | "cancellations" | "suspensions">) =>
    months
      .map((row, index) => `${index === 0 ? "M" : "L"} ${index * step} ${toY(row[key])}`)
      .join(" ");
  return (
    <svg viewBox="0 0 100 56" className="admin-chart-svg admin-chart-svg--wide" role="img" aria-label="Churn timeline">
      <path d={path("signups")} className="admin-chart-line admin-chart-line--up" />
      <path d={path("cancellations")} className="admin-chart-line admin-chart-line--down" />
      <path d={path("suspensions")} className="admin-chart-line admin-chart-line--warn" />
      {months.map((row, index) => (
        index % 2 === 0 ? (
          <text key={row.month} x={index * step} y="54" textAnchor="middle" className="admin-chart-label">
            {row.label.replace(/ \d+$/, "")}
          </text>
        ) : null
      ))}
    </svg>
  );
}

function ServiceMeta({ service }: { service: HealthService }) {
  const bits: string[] = [];
  const meta = service.meta;
  if (typeof meta.version === "string" && meta.version) bits.push(meta.version);
  if (typeof meta.connections === "number") bits.push(`${meta.connections} connections`);
  if (typeof meta.size_bytes === "number") bits.push(formatBytes(meta.size_bytes));
  if (typeof meta.uptime_seconds === "number") bits.push(formatUptime(meta.uptime_seconds));
  if (typeof meta.queue_depth === "number") bits.push(`queue ${meta.queue_depth}`);
  if (typeof meta.bucket === "string" && meta.bucket) bits.push(meta.bucket);
  if (bits.length === 0) return null;
  return <p className="admin-muted">{bits.join(" · ")}</p>;
}

function StatusBadge({ status, children }: { status: string; children?: string }) {
  return <span className={`admin-badge is-${status}`}>{children ?? status}</span>;
}
